import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Pagination,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Tooltip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  CloudSync as CloudSyncIcon,
  Google as GoogleIcon,
  Instagram as InstagramIcon,
  Replay as ReplayIcon,
  Terminal as TerminalIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import {
  crawlManagementService,
  CrawlLog,
  OutletCrawlStatus,
  FileLogEntry,
  StatusSummary,
  CrawlType,
} from '../../services/api/crawlManagement.service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CRAWL_TYPE_LABELS: Record<string, { label: string; color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' }> = {
  google_reviews: { label: 'Google Reviews', color: 'primary' },
  instagram: { label: 'Instagram', color: 'secondary' },
  tiktok: { label: 'TikTok', color: 'info' },
  csv_google_reviews: { label: 'CSV Google Reviews', color: 'warning' },
  all: { label: 'All Crawlers', color: 'success' },
  google_reviews_single: { label: 'Google (Single)', color: 'primary' },
};

const STATUS_CHIPS: Record<string, { color: 'success' | 'error' | 'warning' | 'info' | 'default'; icon: React.ReactElement }> = {
  completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  failed: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
  running: { color: 'warning', icon: <CircularProgress size={14} /> },
  pending: { color: 'info', icon: <ScheduleIcon fontSize="small" /> },
};

function formatDuration(start: string, end: string | null): string {
  if (!end) return 'Running...';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const CrawlManagementPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Crawl Logs state
  const [logs, setLogs] = useState<CrawlLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsTypeFilter, setLogsTypeFilter] = useState('');
  const [logsStatusFilter, setLogsStatusFilter] = useState('');

  // Trigger state
  const [triggerLoading, setTriggerLoading] = useState<string | null>(null);
  const [activeCrawls, setActiveCrawls] = useState<Record<string, boolean>>({});

  // Outlet status state
  const [outlets, setOutlets] = useState<OutletCrawlStatus[]>([]);
  const [outletsLoading, setOutletsLoading] = useState(false);
  const [outletsPage, setOutletsPage] = useState(1);
  const [outletsTotalPages, setOutletsTotalPages] = useState(1);
  const [outletsStatusFilter, setOutletsStatusFilter] = useState('');
  const [outletsBrandFilter, setOutletsBrandFilter] = useState('');
  const [statusSummary, setStatusSummary] = useState<StatusSummary[]>([]);
  const [recrawlLoading, setRecrawlLoading] = useState<number | null>(null);
  const [recrawlDialogOpen, setRecrawlDialogOpen] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState<OutletCrawlStatus | null>(null);
  const [recrawlStartDate, setRecrawlStartDate] = useState<Dayjs | null>(null);
  const [recrawlEndDate, setRecrawlEndDate] = useState<Dayjs | null>(null);

  // File logs state
  const [fileLogs, setFileLogs] = useState<FileLogEntry[]>([]);
  const [fileLogsLoading, setFileLogsLoading] = useState(false);
  const [selectedFileLog, setSelectedFileLog] = useState<string | null>(null);
  const [fileLogContent, setFileLogContent] = useState<string[]>([]);
  const [fileLogDialogOpen, setFileLogDialogOpen] = useState(false);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // ==================== CRAWL LOGS ====================

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const response = await crawlManagementService.getLogs({
        page: logsPage,
        limit: 15,
        crawl_type: logsTypeFilter || undefined,
        status: logsStatusFilter || undefined,
      });
      setLogs(response.data);
      setLogsTotalPages(response.pagination.pages);
    } catch (err) {
      setError('Failed to load crawl logs');
    } finally {
      setLogsLoading(false);
    }
  }, [logsPage, logsTypeFilter, logsStatusFilter]);

  useEffect(() => {
    if (tabIndex === 0) loadLogs();
  }, [tabIndex, loadLogs]);

  // ==================== TRIGGER ====================

  const loadActiveCrawls = useCallback(async () => {
    try {
      const response = await crawlManagementService.getActiveCrawls();
      setActiveCrawls(response.data.active_processes);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 1) loadActiveCrawls();
  }, [tabIndex, loadActiveCrawls]);

  const handleTriggerCrawl = async (crawlType: CrawlType) => {
    setTriggerLoading(crawlType);
    setError(null);
    try {
      const response = await crawlManagementService.triggerCrawl(crawlType);
      setSuccessMessage(response.message);
      loadActiveCrawls();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to trigger crawl';
      setError(msg);
    } finally {
      setTriggerLoading(null);
    }
  };

  // ==================== OUTLET STATUS ====================

  const loadOutlets = useCallback(async () => {
    setOutletsLoading(true);
    try {
      const response = await crawlManagementService.getOutletStatus({
        page: outletsPage,
        limit: 15,
        status: outletsStatusFilter || undefined,
        brand: outletsBrandFilter || undefined,
      });
      setOutlets(response.data);
      setOutletsTotalPages(response.pagination.pages);
      setStatusSummary(response.summary);
    } catch {
      setError('Failed to load outlet status');
    } finally {
      setOutletsLoading(false);
    }
  }, [outletsPage, outletsStatusFilter, outletsBrandFilter]);

  useEffect(() => {
    if (tabIndex === 2) loadOutlets();
  }, [tabIndex, loadOutlets]);

  const openRecrawlDialog = (outlet: OutletCrawlStatus) => {
    setSelectedOutlet(outlet);
    setRecrawlStartDate(null);
    setRecrawlEndDate(null);
    setRecrawlDialogOpen(true);
    setError(null);
  };

  const closeRecrawlDialog = (force = false) => {
    if (recrawlLoading && !force) {
      return;
    }

    setRecrawlDialogOpen(false);
    setSelectedOutlet(null);
    setRecrawlStartDate(null);
    setRecrawlEndDate(null);
  };

  const handleRecrawl = async () => {
    if (!selectedOutlet) {
      return;
    }

    const hasStartDate = Boolean(recrawlStartDate);
    const hasEndDate = Boolean(recrawlEndDate);

    if (hasStartDate !== hasEndDate) {
      setError('Select both start and end date');
      return;
    }

    if (recrawlStartDate && recrawlEndDate && recrawlEndDate.isBefore(recrawlStartDate, 'day')) {
      setError('End date cannot be before start date');
      return;
    }

    setRecrawlLoading(selectedOutlet.id);
    setError(null);
    try {
      const response = await crawlManagementService.recrawlOutlet({
        outlet_id: selectedOutlet.id,
        start_date: recrawlStartDate ? recrawlStartDate.format('YYYY-MM-DD') : undefined,
        end_date: recrawlEndDate ? recrawlEndDate.format('YYYY-MM-DD') : undefined,
      });
      setSuccessMessage(response.message);
      closeRecrawlDialog(true);
      loadOutlets();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to recrawl outlet';
      setError(msg);
    } finally {
      setRecrawlLoading(null);
    }
  };

  // ==================== FILE LOGS ====================

  const loadFileLogs = useCallback(async () => {
    setFileLogsLoading(true);
    try {
      const response = await crawlManagementService.getFileLogs();
      setFileLogs(response.data);
    } catch {
      setError('Failed to load file logs');
    } finally {
      setFileLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 3) loadFileLogs();
  }, [tabIndex, loadFileLogs]);

  const handleViewFileLog = async (filename: string) => {
    setSelectedFileLog(filename);
    setFileLogDialogOpen(true);
    try {
      const response = await crawlManagementService.getFileLogContent(filename, 300);
      setFileLogContent(response.data.lines);
    } catch {
      setFileLogContent(['Error loading log file']);
    }
  };

  const getOutletStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    if (status === 'DONE') return 'success';
    if (status?.startsWith('ERROR')) return 'error';
    if (status === 'IN_PROGRESS') return 'warning';
    if (status === 'PENDING') return 'info';
    return 'default';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <CloudSyncIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>
            Crawl Management
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="Crawl Logs" />
          <Tab icon={<PlayArrowIcon />} iconPosition="start" label="Trigger Crawl" />
          <Tab icon={<StorageIcon />} iconPosition="start" label="Outlet Status" />
          <Tab icon={<TerminalIcon />} iconPosition="start" label="File Logs" />
        </Tabs>
      </Paper>

      {/* ==================== TAB 0: CRAWL LOGS ==================== */}
      <TabPanel value={tabIndex} index={0}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Crawl Type</InputLabel>
                <Select
                  value={logsTypeFilter}
                  label="Crawl Type"
                  onChange={(e) => { setLogsTypeFilter(e.target.value); setLogsPage(1); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="google_reviews">Google Reviews</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                  <MenuItem value="tiktok">TikTok</MenuItem>
                  <MenuItem value="csv_google_reviews">CSV Google Reviews</MenuItem>
                  <MenuItem value="all">All Crawlers</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={logsStatusFilter}
                  label="Status"
                  onChange={(e) => { setLogsStatusFilter(e.target.value); setLogsPage(1); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="running">Running</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadLogs}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper>
          {logsLoading && <LinearProgress />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Triggered By</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Outlets</TableCell>
                  <TableCell>Reviews</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 && !logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No crawl logs found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const statusInfo = STATUS_CHIPS[log.status] || STATUS_CHIPS.pending;
                    const typeInfo = CRAWL_TYPE_LABELS[log.crawl_type] || { label: log.crawl_type, color: 'default' as const };
                    return (
                      <TableRow key={log.id} hover>
                        <TableCell>{log.id}</TableCell>
                        <TableCell>
                          <Chip label={typeInfo.label} size="small" color={typeInfo.color} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={statusInfo.icon}
                            label={log.status}
                            size="small"
                            color={statusInfo.color}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{log.triggered_by}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(log.started_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDuration(log.started_at, log.completed_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {log.total_outlets > 0 ? (
                            <Tooltip title={`${log.successful_outlets} success / ${log.failed_outlets} failed`}>
                              <Typography variant="body2">
                                {log.successful_outlets}/{log.total_outlets}
                              </Typography>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {log.total_reviews > 0 ? (
                            <Tooltip title={`${log.new_reviews} new`}>
                              <Typography variant="body2">
                                {log.total_reviews} ({log.new_reviews} new)
                              </Typography>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          {log.error_message ? (
                            <Tooltip title={log.error_message}>
                              <Typography variant="body2" color="error" noWrap>
                                {log.error_message.slice(0, 60)}...
                              </Typography>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {logsTotalPages > 1 && (
            <Box display="flex" justifyContent="center" py={2}>
              <Pagination
                count={logsTotalPages}
                page={logsPage}
                onChange={(_, p) => setLogsPage(p)}
                color="primary"
              />
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* ==================== TAB 1: TRIGGER CRAWL ==================== */}
      <TabPanel value={tabIndex} index={1}>
        <Grid container spacing={3}>
          {([
            { type: 'google_reviews' as CrawlType, title: 'Google Reviews', desc: 'Crawl Google Maps reviews via Apify for all pending outlets', icon: <GoogleIcon sx={{ fontSize: 40 }} /> },
            { type: 'instagram' as CrawlType, title: 'Instagram', desc: 'Crawl Instagram posts and comments via Apify batch export', icon: <InstagramIcon sx={{ fontSize: 40 }} /> },
            { type: 'tiktok' as CrawlType, title: 'TikTok Comments', desc: 'Crawl TikTok comments via Apify scraper', icon: <PlayArrowIcon sx={{ fontSize: 40 }} /> },
            { type: 'csv_google_reviews' as CrawlType, title: 'CSV Google Reviews', desc: 'Import Google reviews from CSV files', icon: <DescriptionIcon sx={{ fontSize: 40 }} /> },
            { type: 'all' as CrawlType, title: 'Run All Crawlers', desc: 'Run Google Reviews, TikTok, and CSV import together', icon: <CloudSyncIcon sx={{ fontSize: 40 }} /> },
          ]).map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.type}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0px 8px 24px rgba(0,0,0,0.1)' },
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" gutterBottom>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {item.desc}
                  </Typography>
                  {activeCrawls[item.type] && (
                    <Chip
                      icon={<CircularProgress size={14} />}
                      label="Running"
                      color="warning"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={triggerLoading === item.type ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                    onClick={() => handleTriggerCrawl(item.type)}
                    disabled={!!triggerLoading || !!activeCrawls[item.type]}
                  >
                    {activeCrawls[item.type] ? 'Running...' : 'Start Crawl'}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* ==================== TAB 2: OUTLET STATUS ==================== */}
      <TabPanel value={tabIndex} index={2}>
        {statusSummary.length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Status Summary
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {statusSummary.map((s) => (
                <Chip
                  key={s.status || 'null'}
                  label={`${s.status || 'No Status'}: ${s.count}`}
                  size="small"
                  color={getOutletStatusColor(s.status)}
                  variant="outlined"
                  onClick={() => { setOutletsStatusFilter(s.status || ''); setOutletsPage(1); }}
                />
              ))}
            </Box>
          </Paper>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter by brand..."
                value={outletsBrandFilter}
                onChange={(e) => { setOutletsBrandFilter(e.target.value); setOutletsPage(1); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={outletsStatusFilter}
                  label="Status"
                  onChange={(e) => { setOutletsStatusFilter(e.target.value); setOutletsPage(1); }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DONE">Done</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="ERROR">Error</MenuItem>
                  <MenuItem value="ERROR_NO_DATA">Error No Data</MenuItem>
                  <MenuItem value="ERROR_INVALID_URL">Error Invalid URL</MenuItem>
                  <MenuItem value="ERROR_NO_REVIEWS">Error No Reviews</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button fullWidth variant="outlined" startIcon={<RefreshIcon />} onClick={loadOutlets}>
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper>
          {outletsLoading && <LinearProgress />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Outlet</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Outlet Code</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outlets.length === 0 && !outletsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No outlets found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  outlets.map((outlet) => (
                    <TableRow key={outlet.id} hover>
                      <TableCell>{outlet.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{outlet.brand}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={outlet.alamat || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {outlet.outlet}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={outlet.status || 'No Status'}
                          size="small"
                          color={getOutletStatusColor(outlet.status)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{outlet.outlet_code || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {outlet.updated_at ? new Date(outlet.updated_at).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Recrawl this outlet">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openRecrawlDialog(outlet)}
                            disabled={recrawlLoading === outlet.id}
                          >
                            {recrawlLoading === outlet.id ? <CircularProgress size={18} /> : <ReplayIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {outletsTotalPages > 1 && (
            <Box display="flex" justifyContent="center" py={2}>
              <Pagination
                count={outletsTotalPages}
                page={outletsPage}
                onChange={(_, p) => setOutletsPage(p)}
                color="primary"
              />
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* ==================== TAB 3: FILE LOGS ==================== */}
      <TabPanel value={tabIndex} index={3}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={500}>
              Crawler Agent Log Files
            </Typography>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadFileLogs}>
              Refresh
            </Button>
          </Box>
        </Paper>

        <Paper>
          {fileLogsLoading && <LinearProgress />}
          <List>
            {fileLogs.length === 0 && !fileLogsLoading ? (
              <ListItem>
                <ListItemText primary="No log files found" secondary="Check CRAWLER_AGENT_DIR configuration" />
              </ListItem>
            ) : (
              fileLogs.map((file) => (
                <React.Fragment key={file.filename}>
                  <ListItem
                    button
                    onClick={() => handleViewFileLog(file.filename)}
                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <ListItemIcon>
                      <TerminalIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.filename}
                      secondary={`${formatBytes(file.size)} · Modified: ${new Date(file.modified_at).toLocaleString()}`}
                    />
                    <Chip label="View" size="small" variant="outlined" />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>

        <Dialog
          open={fileLogDialogOpen}
          onClose={() => setFileLogDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <TerminalIcon />
                <Typography variant="h6">{selectedFileLog}</Typography>
              </Box>
              <IconButton onClick={() => setFileLogDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                fontFamily: '"Fira Code", "Consolas", monospace',
                fontSize: '0.8rem',
                p: 2,
                borderRadius: 1,
                maxHeight: '60vh',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {fileLogContent.length === 0 ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={24} sx={{ color: '#d4d4d4' }} />
                </Box>
              ) : (
                fileLogContent.map((line, i) => (
                  <Box key={i} sx={{ py: 0.1, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                    <Typography
                      component="span"
                      sx={{
                        color: '#858585',
                        mr: 2,
                        userSelect: 'none',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        display: 'inline-block',
                        width: 40,
                        textAlign: 'right',
                      }}
                    >
                      {i + 1}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        color: line.includes('ERROR') ? '#f44747' : line.includes('WARNING') ? '#cca700' : line.includes('SUCCESS') || line.includes('Successfully') ? '#6a9955' : '#d4d4d4',
                      }}
                    >
                      {line}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFileLogDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>
      <Dialog
        open={recrawlDialogOpen}
        onClose={() => closeRecrawlDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Recrawl Outlet</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Outlet"
              value={selectedOutlet?.outlet || ''}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
            <Typography variant="body2" color="text.secondary">
              Leave dates empty to use the crawler default range.
            </Typography>
            <DatePicker
              label="Start Date"
              value={recrawlStartDate}
              onChange={(value) => setRecrawlStartDate(value)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={recrawlEndDate}
              onChange={(value) => setRecrawlEndDate(value)}
              minDate={recrawlStartDate || undefined}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
            {recrawlStartDate && recrawlEndDate && (
              <Typography variant="caption" color="text.secondary">
                Range: {dayjs(recrawlStartDate).format('DD MMM YYYY')} to {dayjs(recrawlEndDate).format('DD MMM YYYY')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeRecrawlDialog()} disabled={!!recrawlLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRecrawl}
            disabled={!selectedOutlet || !!recrawlLoading}
            startIcon={recrawlLoading && selectedOutlet?.id === recrawlLoading ? <CircularProgress size={18} color="inherit" /> : <ReplayIcon />}
          >
            Start Recrawl
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CrawlManagementPage;
